import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Helmet } from 'react-helmet';

/**
 * Route map: EN path → { fragment, seoTitle, seoDesc, frPath }
 *            FR path → { fragment, seoTitle, seoDesc, enPath }
 */
const PAGES = {
  // ── English ──
  '/review-culture': {
    fragment: '/review-culture-fragments/en/index.html',
    seoTitle: 'Review Culture | Hear Them First. Make It Right. | YUL On Tap',
    seoDesc: 'Research on why customers who feel heard respond differently, and how businesses can make things right before a concern goes public.',
    pair: '/fr/culture-des-avis',
    lang: 'en',
  },
  '/review-culture/why-being-heard-matters': {
    fragment: '/review-culture-fragments/en/why-being-heard-matters.html',
    seoTitle: 'Why Being Heard Matters | Review Culture | YUL On Tap',
    seoDesc: 'A negative review is the visible end of an experience. Research shows that how a business responds can change what happens next.',
    pair: '/fr/culture-des-avis/pourquoi-etre-entendu',
    lang: 'en',
  },
  '/review-culture/research': {
    fragment: '/review-culture-fragments/en/research.html',
    seoTitle: 'The Research | Review Culture | YUL On Tap',
    seoDesc: 'Six peer-reviewed studies on active listening, complaint handling and service recovery, and what they mean for businesses.',
    pair: '/fr/culture-des-avis/recherche',
    lang: 'en',
  },
  '/review-culture/industries': {
    fragment: '/review-culture-fragments/en/industries.html',
    seoTitle: 'Review Culture by Industry | Hotels, Restaurants, Automotive, Clinics, Foundations | YUL On Tap',
    seoDesc: 'What the research says about reviews and complaint handling in hotels, restaurants, automotive, medical clinics and foundations.',
    pair: '/fr/culture-des-avis/secteurs',
    lang: 'en',
  },
  '/review-culture/how-b-heard-works': {
    fragment: '/review-culture-fragments/en/how-b-heard-works.html',
    seoTitle: 'How B-Heard Works | Review Culture | YUL On Tap',
    seoDesc: 'B-Heard gives every customer a private way to be heard, routes concerns to the right person and never blocks a public review.',
    pair: '/fr/culture-des-avis/comment-fonctionne-b-heard',
    lang: 'en',
  },
  '/review-culture/measuring-results': {
    fragment: '/review-culture-fragments/en/measuring-results.html',
    seoTitle: 'Measuring Results | Review Culture | YUL On Tap',
    seoDesc: 'How we test whether private feedback and service recovery work, and what we will not claim.',
    pair: '/fr/culture-des-avis/mesurer-les-resultats',
    lang: 'en',
  },
  // ── French ──
  '/fr/culture-des-avis': {
    fragment: '/review-culture-fragments/fr/index.html',
    seoTitle: 'Culture des avis | Écoutez d\'abord. Corrigez le tir. | YUL On Tap',
    seoDesc: 'La recherche sur l\'effet d\'être écouté, et comment les entreprises peuvent corriger la situation avant qu\'une préoccupation ne devienne publique.',
    pair: '/review-culture',
    lang: 'fr',
  },
  '/fr/culture-des-avis/pourquoi-etre-entendu': {
    fragment: '/review-culture-fragments/fr/pourquoi-etre-entendu.html',
    seoTitle: 'Pourquoi être entendu compte | Culture des avis | YUL On Tap',
    seoDesc: 'Un avis négatif est la partie visible d\'une expérience. La recherche montre que la façon de répondre peut changer la suite des choses.',
    pair: '/review-culture/why-being-heard-matters',
    lang: 'fr',
  },
  '/fr/culture-des-avis/recherche': {
    fragment: '/review-culture-fragments/fr/recherche.html',
    seoTitle: 'La recherche | Culture des avis | YUL On Tap',
    seoDesc: 'Six études évaluées par les pairs sur l\'écoute active, le traitement des plaintes et le redressement de service, et ce qu\'elles signifient pour les entreprises.',
    pair: '/review-culture/research',
    lang: 'fr',
  },
  '/fr/culture-des-avis/secteurs': {
    fragment: '/review-culture-fragments/fr/secteurs.html',
    seoTitle: 'Culture des avis par secteur | Hôtels, restaurants, concessionnaires, cliniques, fondations | YUL On Tap',
    seoDesc: 'Ce que dit la recherche sur les avis et le traitement des plaintes dans les hôtels, restaurants, concessionnaires, cliniques médicales et fondations.',
    pair: '/review-culture/industries',
    lang: 'fr',
  },
  '/fr/culture-des-avis/comment-fonctionne-b-heard': {
    fragment: '/review-culture-fragments/fr/comment-fonctionne-b-heard.html',
    seoTitle: 'Comment fonctionne B-Heard | Culture des avis | YUL On Tap',
    seoDesc: 'B-Heard offre à chaque client une façon privée d\'être entendu, transmet les préoccupations à la bonne personne et ne bloque jamais un avis public.',
    pair: '/review-culture/how-b-heard-works',
    lang: 'fr',
  },
  '/fr/culture-des-avis/mesurer-les-resultats': {
    fragment: '/review-culture-fragments/fr/mesurer-les-resultats.html',
    seoTitle: 'Mesurer les résultats | Culture des avis | YUL On Tap',
    seoDesc: 'Comment nous vérifions que la rétroaction privée et le redressement de service fonctionnent, et ce que nous n\'affirmerons pas.',
    pair: '/review-culture/measuring-results',
    lang: 'fr',
  },
};

/** Find the page config for the current path */
function findPage(pathname) {
  // Exact match
  if (PAGES[pathname]) return { path: pathname, ...PAGES[pathname] };
  // Try without trailing slash
  const clean = pathname.replace(/\/$/, '');
  if (PAGES[clean]) return { path: clean, ...PAGES[clean] };
  return null;
}

/** Get the paired language path for the language toggle */
export function getReviewCulturePair(pathname) {
  const page = findPage(pathname);
  return page ? page.pair : null;
}

/** Check if a path is a Review Culture page */
export function isReviewCulturePath(pathname) {
  return findPage(pathname) !== null;
}

/** Get the language of a Review Culture page */
export function getReviewCultureLang(pathname) {
  const page = findPage(pathname);
  return page ? page.lang : null;
}

const ReviewCulturePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const [html, setHtml] = useState('');
  const [pageData, setPageData] = useState(null);
  const containerRef = useRef(null);

  // Sync language context with route language
  useEffect(() => {
    const page = findPage(location.pathname);
    if (page && page.lang !== language) {
      setLanguage(page.lang);
    }
  }, [location.pathname, language, setLanguage]);

  // Load fragment on route change
  useEffect(() => {
    const page = findPage(location.pathname);
    if (!page) {
      navigate('/', { replace: true });
      return;
    }
    setPageData(page);

    fetch(page.fragment)
      .then(r => r.text())
      .then(text => {
        // Strip the comment lines, keep only the <main> content
        const mainMatch = text.match(/<main[\s\S]*<\/main>/);
        setHtml(mainMatch ? mainMatch[0] : text);
      })
      .catch(() => setHtml('<p>Failed to load content.</p>'));
  }, [location.pathname, navigate]);

  // Load CSS once
  useEffect(() => {
    const id = 'review-culture-css';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = '/review-culture-assets/review-culture.css';
      document.head.appendChild(link);
    }
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  // Handle internal link clicks via React Router (SPA navigation)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleClick = (e) => {
      const a = e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href');
      // Only intercept internal review-culture links
      if (href && (href.startsWith('/review-culture') || href.startsWith('/fr/culture-des-avis'))) {
        e.preventDefault();
        navigate(href);
        window.scrollTo(0, 0);
      }
    };

    container.addEventListener('click', handleClick);
    return () => container.removeEventListener('click', handleClick);
  }, [html, navigate]);

  // Execute inline scripts (for the PDF download check)
  useEffect(() => {
    if (!containerRef.current || !html) return;
    const scripts = containerRef.current.querySelectorAll('script');
    scripts.forEach(oldScript => {
      const newScript = document.createElement('script');
      if (oldScript.src) {
        newScript.src = oldScript.src;
      } else {
        newScript.textContent = oldScript.textContent;
      }
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }, [html]);

  if (!pageData) return null;

  return (
    <>
      <Helmet>
        <title>{pageData.seoTitle}</title>
        <meta name="description" content={pageData.seoDesc} />
      </Helmet>
      <div
        ref={containerRef}
        data-testid="review-culture-page"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
};

export default ReviewCulturePage;
export { PAGES as REVIEW_CULTURE_PAGES };
